import { Node, mergeAttributes } from "@tiptap/core";

export interface TableOfContentsOptions {
  /** Heading shown above the entries. */
  title: string;
  /** Text shown when the document has no headings yet. */
  emptyText: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableOfContents: {
      /** Insert an auto-updating table of contents at the cursor. */
      insertTableOfContents: () => ReturnType;
    };
  }
}

interface TocHeading {
  level: number;
  text: string;
  pos: number;
}

/**
 * An in-document, auto-updating Table of Contents (Docs/Word parity).
 *
 * It is an ATOM block node — it holds no editable content; instead its node view
 * scans the document's headings on every editor update and renders a clickable,
 * indented list. Clicking an entry scrolls to that heading. On DOCX export the
 * node is expanded to a static heading list (see docxExport). The node itself
 * serializes as an empty marker, so it re-derives from the live headings on load.
 */
export const TableOfContentsExtension = Node.create<TableOfContentsOptions>({
  name: "tableOfContents",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,
  isolating: true,

  addOptions() {
    return {
      title: "Table of Contents",
      emptyText: "Add headings (Heading 1–6) to build the table of contents.",
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="table-of-contents"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    // Static serialization marker. The live list is drawn by the node view;
    // DOCX export expands it into a real heading list.
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "table-of-contents",
        class: "tiptap-toc",
      }),
    ];
  },

  addNodeView() {
    return ({ editor }) => {
      const dom = document.createElement("div");
      dom.className = "tiptap-toc";
      dom.setAttribute("data-type", "table-of-contents");
      dom.contentEditable = "false";

      const collectHeadings = (): TocHeading[] => {
        const items: TocHeading[] = [];
        editor.state.doc.descendants((node, pos) => {
          if (node.type.name === "heading") {
            items.push({
              level: node.attrs.level || 1,
              text: node.textContent.trim(),
              pos,
            });
          }
        });
        return items;
      };

      const render = () => {
        const items = collectHeadings();
        dom.innerHTML = "";

        const title = document.createElement("div");
        title.className = "tiptap-toc-title";
        title.textContent = this.options.title;
        dom.appendChild(title);

        if (items.length === 0) {
          const empty = document.createElement("div");
          empty.className = "tiptap-toc-empty";
          empty.textContent = this.options.emptyText;
          dom.appendChild(empty);
          return;
        }

        // Normalize so the shallowest heading present sits at indent 0.
        const minLevel = Math.min(...items.map(i => i.level));
        for (const item of items) {
          const entry = document.createElement("div");
          entry.className = "tiptap-toc-entry";
          entry.style.paddingLeft = `${(item.level - minLevel) * 18}px`;

          const link = document.createElement("a");
          link.className = "tiptap-toc-link";
          link.href = "#";
          link.textContent = item.text || "(untitled)";
          link.addEventListener("mousedown", e => {
            // mousedown + preventDefault keeps the editor selection from
            // fighting the click, matching the header/footer overlay pattern.
            e.preventDefault();
            editor
              .chain()
              .setTextSelection(item.pos + 1)
              .scrollIntoView()
              .run();
          });
          entry.appendChild(link);
          dom.appendChild(entry);
        }
      };

      render();
      const onUpdate = () => render();
      editor.on("update", onUpdate);

      return {
        dom,
        ignoreMutation: () => true,
        update: node => node.type.name === "tableOfContents",
        destroy: () => {
          editor.off("update", onUpdate);
        },
      };
    };
  },

  addCommands() {
    return {
      insertTableOfContents:
        () =>
        ({ commands }) =>
          commands.insertContent({ type: this.name }),
    };
  },
});

export default TableOfContentsExtension;
